import jsPDF from 'jspdf';
import { getLeadTypeLabel, formatLeadDate, formatCurrency } from '@/components/admin/LeadDetailsContent';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';

const displayDate = (value) => (value ? formatDateMdY(value, 'N/A') : 'N/A');

const displaySsn = (last4, revealed) => {
  if (revealed) return revealed;
  const tail = last4 ? String(last4).replace(/\D/g, '').slice(-4) : '';
  return tail ? `***-**-${tail}` : 'N/A';
};

const displayDl = (last4, revealed) => {
  if (revealed) return revealed;
  const tail = last4 ? String(last4).replace(/\D/g, '').slice(-4) : '';
  return tail ? `••••${tail}` : 'Not provided';
};

const sectionHasValues = (section) => {
  if (!section || typeof section !== 'object') return false;
  return Object.entries(section).some(([key, value]) => key !== 'source' && String(value || '').trim());
};

const otherIncomeHasData = (info) => {
  if (!info) return false;
  return [
    'other_income_amount', 'other_income_frequency', 'source_of_other_income', 'reference',
    'relationship', 'phone_number', 'address', 'bank_reference', 'bank_phone_number', 'bank_account_type',
  ].some((key) => String(info[key] || '').trim());
};

export function generateLeadApplicationPdf(snapshot) {
  const lead = snapshot?.lead || {};
  const details = lead.details || {};
  const revealSsn = Boolean(snapshot.reveal_ssn);
  const revealDl = Boolean(snapshot.reveal_driver_license);
  const ssnLast4 = snapshot.ssn_last4 || details.applicant?.ssn_last4 || details.personalInfo?.ssn_last4;
  const dlLast4 = snapshot.driver_license_last4
    || snapshot.driver_license_info?.dl_last4
    || details.applicant?.driverLicenseLast4
    || details.personalInfo?.driverLicenseLast4;
  const ssnValue = displaySsn(ssnLast4, revealSsn ? snapshot.revealed_ssn : '');
  const dlValue = displayDl(dlLast4, revealDl ? snapshot.revealed_driver_license : '');

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;
  const lineHeight = 7;
  const sectionGap = 10;
  const leftMargin = 20;
  const rightCol = 110;
  const SPACES_AFTER_COLON = 3;

  const addTitle = (text) => {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(text, pageWidth / 2, y, { align: 'center' });
    y += lineHeight + 5;
  };

  const addSectionHeader = (text) => {
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(59, 130, 246);
    doc.text(text, leftMargin, y);
    y += lineHeight;
    doc.setTextColor(0, 0, 0);
  };

  const gapAfterColon = () => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    return doc.getTextWidth(' ') * SPACES_AFTER_COLON;
  };

  const valueXAfterLabel = (label, x) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    return x + doc.getTextWidth(`${label}:`) + gapAfterColon();
  };

  const addLabeledValue = (label, value, x) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, x, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value || 'N/A'), valueXAfterLabel(label, x), y);
  };

  const addField = (label, value, x = leftMargin) => {
    if (y > 275) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, x, y);
    doc.setFont('helvetica', 'normal');
    const displayValue = value || 'N/A';
    const valueX = valueXAfterLabel(label, x);
    const wrapped = doc.splitTextToSize(String(displayValue), Math.max(40, pageWidth - valueX - 12));
    doc.text(wrapped, valueX, y);
    y += Math.max(lineHeight, wrapped.length * lineHeight);
  };

  const addFieldRow = (label1, value1, label2, value2) => {
    if (y > 275) {
      doc.addPage();
      y = 20;
    }
    addLabeledValue(label1, value1, leftMargin);
    if (label2) {
      addLabeledValue(label2, value2, rightCol);
    }
    y += lineHeight;
  };

  const addParagraph = (text, { x = leftMargin, font = 'normal' } = {}) => {
    const lines = doc.splitTextToSize(String(text || 'N/A'), pageWidth - x - 12);
    lines.forEach((line) => {
      if (y > 275) {
        doc.addPage();
        y = 20;
      }
      doc.setFontSize(10);
      doc.setFont('helvetica', font);
      doc.text(line, x, y);
      y += lineHeight;
    });
  };

  addTitle('Drive Now Autohaus');
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(getLeadTypeLabel(lead.type), pageWidth / 2, y, { align: 'center' });
  y += lineHeight + 5;

  addLabeledValue('Generated', formatDateTimeMdY(new Date()), leftMargin);
  addLabeledValue('Status', (lead.status || '').replace(/_/g, ' ').toUpperCase(), rightCol);
  y += sectionGap;

  addSectionHeader('Contact Information');
  addFieldRow('Name', lead.name, 'Email', lead.email);
  addFieldRow('Phone', lead.phone || 'N/A', 'Submitted', lead.created_at ? formatLeadDate(lead.created_at) : 'N/A');
  y += sectionGap;

  switch (lead.type) {
    case 'contact': {
      addSectionHeader('Message');
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const message = details.message || 'No message provided';
      const splitMessage = doc.splitTextToSize(message, pageWidth - 40);
      doc.text(splitMessage, leftMargin, y);
      y += splitMessage.length * lineHeight;
      break;
    }
    case 'vehicle_inquiry': {
      addSectionHeader('Inquiry Details');
      addField('Vehicle', details.vehicle_info);
      addField('Preferred Contact', details.preferred_contact || 'Email');
      if (details.message) {
        y += 3;
        addField('Message', details.message);
      }
      break;
    }
    case 'financing': {
      const applicant = details.applicant || {};
      const employment = details.employment || {};
      const vehicle = details.vehicle || {};
      const coBuyer = details.coBuyer;

      addSectionHeader('Applicant Information');
      addFieldRow('First Name', applicant.firstName, 'Middle Name', applicant.middleName);
      addFieldRow('Last Name', applicant.lastName, 'Date of Birth', displayDate(applicant.dateOfBirth));
      addFieldRow('SSN', ssnValue, 'Mobile', applicant.mobileNumber);
      addFieldRow('Home Phone', applicant.homeNumber, 'Email', applicant.email);
      y += 3;
      addField('Address', `${applicant.addressLine1 || ''} ${applicant.addressLine2 || ''}`.trim());
      addFieldRow('City', applicant.city, 'State', applicant.state);
      addFieldRow('ZIP', applicant.zip, '', '');
      y += 3;
      addFieldRow('Driver License #', dlValue, 'DL State', applicant.driverLicenseState || snapshot.driver_license_info?.dl_issuing_state);
      addField('DL Issuing County', applicant.dlIssuingCounty || snapshot.driver_license_info?.dl_issuing_county);
      addField('DL Expiry', displayDate(applicant.driverLicenseExpiry || snapshot.driver_license_info?.dl_expiry_date));
      addFieldRow('Residence Type', applicant.residenceType, 'Rent/Mortgage', formatCurrency(applicant.rentMortgage));
      addFieldRow('Time at Residence', `${applicant.residenceYears || 0} yr ${applicant.residenceMonths || 0} mo`, '', '');
      y += sectionGap;

      addSectionHeader('Employment Information');
      addFieldRow('Employer', employment.employer, 'Type', employment.employerType);
      addFieldRow('Occupation', employment.occupation, 'Monthly Income', formatCurrency(employment.monthlyIncome));
      addFieldRow('Work Phone', employment.workPhone, 'Time at Job', `${employment.jobYears || 0} yr ${employment.jobMonths || 0} mo`);
      addField('Work Address', `${employment.addressLine1 || ''} ${employment.addressLine2 || ''}`.trim());
      addFieldRow('City', employment.city, 'State', employment.state);
      addField('ZIP', employment.zip);
      y += sectionGap;

      addSectionHeader('Vehicle Interested');
      addFieldRow('Year', vehicle.year, 'Make', vehicle.make);
      addFieldRow('Model', vehicle.model, 'Trim', vehicle.trim);
      addFieldRow('VIN', vehicle.vin, 'Stock #', vehicle.stockNumber);
      addField('Mileage', vehicle.mileage);
      y += sectionGap;

      if (coBuyer && coBuyer.info) {
        const cbSsn = displaySsn(
          snapshot.cobuyer_ssn_last4 || coBuyer.info.ssn_last4,
          revealSsn ? snapshot.revealed_cobuyer_ssn : '',
        );
        addSectionHeader('Co-Buyer Information');
        addFieldRow('First Name', coBuyer.info.firstName, 'Last Name', coBuyer.info.lastName);
        addFieldRow('Date of Birth', displayDate(coBuyer.info.dateOfBirth), 'SSN', cbSsn);
        addFieldRow('Mobile', coBuyer.info.mobileNumber, 'Email', coBuyer.info.email);
        if (coBuyer.employment) {
          addFieldRow('Employer', coBuyer.employment.employer, 'Occupation', coBuyer.employment.occupation);
          addField('Monthly Income', formatCurrency(coBuyer.employment.monthlyIncome));
        }
        y += sectionGap;
      }

      if (details.additionalComments) {
        addSectionHeader('Additional Comments');
        const comments = doc.splitTextToSize(details.additionalComments, pageWidth - 40);
        doc.setFont('helvetica', 'normal');
        doc.text(comments, leftMargin, y);
        y += comments.length * lineHeight;
        y += sectionGap;
      }
      break;
    }
    case 'trade_in': {
      const personal = details.personalInfo || {};
      const vehicleInfo = details.vehicleInfo || {};
      addSectionHeader('Personal Information');
      addFieldRow('First Name', personal.firstName, 'Last Name', personal.lastName);
      addFieldRow('Email', personal.email, 'Mobile', personal.mobileNumber);
      addFieldRow('Date of Birth', displayDate(personal.dateOfBirth), 'SSN', ssnValue);
      addFieldRow('Driver License #', dlValue, 'DL State', personal.driverLicenseState);
      addField('DL Expiry', displayDate(personal.driverLicenseExpiry));
      addField('Address', `${personal.addressLine1 || ''} ${personal.addressLine2 || ''}`.trim());
      addFieldRow('City', personal.city, 'State', personal.state);
      addFieldRow('ZIP', personal.zip, 'County', personal.county);
      y += sectionGap;

      addSectionHeader('Trade-In Vehicle');
      addFieldRow('Vehicle', details.vehicle || `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.trim(), 'Mileage', details.mileage || vehicleInfo.mileage);
      addFieldRow('VIN', details.vin || vehicleInfo.vin, 'Accidents', details.accidents || vehicleInfo.accidents);
      addField('Financed', details.financed || vehicleInfo.financed);
      if (details.description) {
        y += 3;
        addField('Description', details.description);
      }
      y += sectionGap;
      break;
    }
    case 'prequalification': {
      addSectionHeader('Pre-Qualification Details');
      addFieldRow('Address', details.address, 'Date of Birth', displayDate(details.dateOfBirth));
      addFieldRow('Job Duration', details.jobDuration, 'Monthly Income', formatCurrency(details.monthlyIncome));
      addFieldRow('Credit Score', details.creditScore, 'Budget', formatCurrency(details.budget));
      addFieldRow('Down Payment', formatCurrency(details.downPayment), 'Pay Taxes Upfront', details.payTaxesUpfront);
      addField('Vehicle Interest', details.interestedVehicle);
      y += sectionGap;
      break;
    }
    case 'chatbot': {
      addSectionHeader('Chat Lead Details');
      addField('Vehicle of interest', details.vehicle_info || details.vehicle);
      addField('Page', details.page_url);
      addField('Source', details.source === 'form' ? 'Chat contact form' : 'Chat message');
      if (details.message) {
        addField('Captured message', details.message);
      }
      y += sectionGap;

      addSectionHeader('Chat Summary');
      addParagraph(details.capture_reason || 'Visitor shared contact information with Francisco.');
      y += sectionGap;

      addSectionHeader('Full Chat');
      const messages = Array.isArray(details.chat_messages) ? details.chat_messages : [];
      if (!messages.length) {
        addParagraph('No chat transcript available.');
      } else {
        messages.forEach((msg) => {
          const speaker = msg.role === 'user' ? 'Visitor' : 'Francisco';
          const when = msg.at ? `  (${formatDateTimeMdY(msg.at)})` : '';
          if (y > 270) {
            doc.addPage();
            y = 20;
          }
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(msg.role === 'user' ? 37 : 5, msg.role === 'user' ? 99 : 150, msg.role === 'user' ? 235 : 105);
          doc.text(`${speaker}${when}`, leftMargin, y);
          doc.setTextColor(0, 0, 0);
          y += lineHeight;
          addParagraph(msg.content || '', { font: 'normal' });
          y += 3;
        });
      }
      y += sectionGap;
      break;
    }
    default:
      break;
  }

  const extraDl = snapshot.driver_license_info;
  if (
    extraDl
    && lead.type !== 'financing'
    && lead.type !== 'trade_in'
    && (extraDl.dl_issuing_county || extraDl.dl_issuing_state || extraDl.dl_expiry_date || extraDl.date_of_birth || extraDl.dl_last4)
  ) {
    addSectionHeader('Driver License Information');
    addFieldRow('Driver License #', dlValue, 'Issuing State', extraDl.dl_issuing_state);
    addFieldRow('Issuing County', extraDl.dl_issuing_county, 'Expiry', displayDate(extraDl.dl_expiry_date));
    addField('Date of Birth', displayDate(extraDl.date_of_birth));
    y += sectionGap;
  }

  const credit = snapshot.credit_app_supplement;
  if (credit && (sectionHasValues(credit.landlord) || sectionHasValues(credit.previous_residential) || sectionHasValues(credit.previous_employment))) {
    addSectionHeader('Additional Information for Credit Application');
    if (sectionHasValues(credit.landlord)) {
      addFieldRow('Landlord Name', credit.landlord.landlord_name, 'Landlord Mobile', credit.landlord.landlord_mobile);
    }
    if (sectionHasValues(credit.previous_residential)) {
      const prev = credit.previous_residential;
      addField('Previous Street Address', prev.previous_street_address);
      addFieldRow('Years Stayed', prev.previous_residence_years, 'Months Stayed', prev.previous_residence_months);
      addFieldRow('City', prev.previous_city, 'State', prev.previous_state);
      addFieldRow('ZIP', prev.previous_zip, 'Rent/Mortgage', prev.previous_rent_mortgage);
    }
    if (sectionHasValues(credit.previous_employment)) {
      const prevEmp = credit.previous_employment;
      addField('Previous Employer', prevEmp.previous_employer);
      addFieldRow('Employment Years', prevEmp.previous_job_years, 'Employment Months', prevEmp.previous_job_months);
    }
    y += sectionGap;
  }

  const other = snapshot.other_income_info;
  if (otherIncomeHasData(other)) {
    addSectionHeader('Additional Information for Other Income');
    addFieldRow('Amount', other.other_income_amount, 'Frequency', other.other_income_frequency_label || other.other_income_frequency);
    addField('Source of Other Income', other.source_of_other_income);
    addFieldRow('Reference', other.reference, 'Relationship', other.relationship);
    addFieldRow('Phone', other.phone_number, 'Bank Phone', other.bank_phone_number);
    addField('Address', other.address);
    addFieldRow('Bank Reference', other.bank_reference, 'Account Type', other.bank_account_type);
    y += sectionGap;
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Page ${i} of ${pageCount} | Drive Now Autohaus | Confidential`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' },
    );
  }

  const fileName = snapshot.file_name
    || `${lead.type || 'lead'}_${String(lead.name || 'application').replace(/\s+/g, '_')}_${String(lead.id || '').slice(0, 8)}.pdf`;
  doc.save(fileName);
}
